/**
 * quoteDisplayResolution.js
 *
 * PR-02d: lifecycle-keyed field resolution for customer-visible quote fields.
 *
 * ## The rule (operator, 2026-07-20)
 *
 * Draft/active resolves LIVE; sent/signed/converted uses the STAMP.
 *
 * A sent quote is an immutable record of what the customer received. If the
 * customer is renamed, or their address changes, or a product's label is
 * polished after the send \u2014 the sent quote must keep showing the fields
 * as sent, otherwise our internal record stops matching the PDF in their
 * inbox. Before sending, however, any change to the customer or product
 * should be reflected on the quote as it happens.
 *
 * This module encodes the class-fix, not the single-field fix. The same
 * lifecycle rule applies to every denormalised display field on a quote:
 * `customer_name`, `billing_address`, `service_address`, `payment_term`,
 * and per-line-item `productLabel` / `subAreaLabel` / `salesDescription`.
 *
 * ## Where it lives
 *
 * `resolveQuoteDisplay(quote, customer)` returns a shallow-merged object
 * with the SAME shape as the incoming quote, but with display fields
 * overwritten by live values IF the quote is pre-send.
 *
 * The returned object is what feeds into the customer-facing `viewQuote`
 * projection in `QuoteReviewSend.js`. This keeps the cost/margin firewall
 * intact (learning #3) \u2014 nothing about resolution changes the shape of
 * `viewQuote`; only its input source is corrected.
 *
 * ## Lifecycle
 *
 * | Status                                    | Resolution   |
 * |-------------------------------------------|--------------|
 * | `active`, `expired`, `lost`, `draft`, `pending` (draft alias) | LIVE   |
 * | `sent`, `signed`, `converted`             | STAMPED      |
 * | anything else (defensive)                 | STAMPED      |
 *
 * `expired` and `lost` are LIVE because a lost/expired quote is not a
 * customer-facing artifact \u2014 it's an internal reference. If reactivated
 * to `active`, we want the current customer data.
 *
 * `converted` is STAMPED because at conversion the quote's terms became
 * the job's terms; the historical record must match what was agreed.
 *
 * ## Missing-customer fallback
 *
 * If a pre-send quote's customer has been deleted (or the fetch fails),
 * fall back to the stamped values. Better to show a stale name than to
 * show nothing / crash.
 */

const LIVE_STATUSES = new Set(['active', 'draft', 'pending', 'expired', 'lost']);

/**
 * @param {string} status
 * @returns {'live' | 'stamped'}
 */
export function resolutionModeForStatus(status) {
  return LIVE_STATUSES.has(String(status || '').toLowerCase()) ? 'live' : 'stamped';
}

/**
 * Given a raw quote (as returned by `quotesAPI.getQuote`) and the current
 * customer record, return a merged quote where the customer-visible display
 * fields respect the lifecycle rule.
 *
 * @param {object} quote
 * @param {object} [customer]   The live customer row keyed by `quote.customer_id`.
 *                              May be null/undefined if the customer was deleted
 *                              or the fetch failed \u2014 in which case stamped values
 *                              are used regardless of lifecycle.
 * @returns {object} A shallow-merged quote with resolved display fields.
 *                  Never mutates `quote` or `customer`.
 */
export function resolveQuoteDisplay(quote, customer) {
  if (!quote) return quote;
  const mode = resolutionModeForStatus(quote.status);
  if (mode === 'stamped' || !customer) {
    // Nothing to overwrite \u2014 return quote as-is (immutability contract).
    return quote;
  }

  // Live-resolve customer-scoped display fields.
  // Every field below is stamped on the quote row at create/update time; here
  // we prefer the corresponding live value from the customer record.
  //
  // NOTE ON CONTACT NAME: contacts are addressed via `contact_id` FK; if the
  // caller wants live contact-name resolution, they must pass the merged
  // contact via `quote.contact` or provide `customer.contacts`. This function
  // handles the customer-scoped fields; contact resolution happens closer to
  // the view (see QuoteReviewSend viewQuote).
  const liveCustomerName =
    customer.name || customer.company_name || quote.customer_name;

  // IDI-427: never let a live address that has LOST its street replace a
  // stored one that has it. A partial live match is only used if it carries
  // a street, or the quote has nothing better.
  const liveCandidate = extractAddress(customer, 'billing') || extractAddress(customer, 'primary');
  const storedHasStreet = !!(quote.billing_address && (quote.billing_address.street || quote.billing_address.street1));
  const liveBillingAddress = (liveCandidate && (liveCandidate.street || !storedHasStreet))
    ? liveCandidate
    : quote.billing_address;

  return {
    ...quote,
    customer_name: liveCustomerName,
    // Only overwrite billing_address if we found a usable live one.
    billing_address: liveBillingAddress || quote.billing_address,
    // service_address is quote-scoped (user picked it OR sameAsBilling) \u2014
    // do NOT overwrite from customer. But if same_as_billing is true, keep it
    // in sync with the (possibly-updated) billing address.
    service_address: quote.same_as_billing
      ? (liveBillingAddress || quote.service_address)
      : quote.service_address,
    // payment_term is quote-scoped (user chose it) \u2014 do NOT overwrite.
    // line_items.productLabel etc. are handled by their own resolver in a follow-up
    // (product catalog live-resolve is broader; deferred until we agree on the
    // read path for catalog fields on old quotes).
  };
}

/**
 * Extract an address object from a customer record. Customer records can
 * carry addresses in several shapes historically \u2014 this normalises.
 *
 * @param {object} customer
 * @param {'billing' | 'primary' | 'service'} kind
 * @returns {object | null}
 */
function extractAddress(customer, kind) {
  if (!customer) return null;
  // Try shape 1: nested object (`customer.billing_address = { street, city, ... }`).
  // IDI-427: the customer API (and the quote's own billing_address) spell the
  // line `street1` + `street2`, not `street`. This branch matched on
  // city/zip, returned street: '', and that EMPTY street then REPLACED the
  // quote's full address in resolveQuoteDisplay. So Review & Send's Bill To
  // read "Louisville KY 40202" with no street (Q-0103, 09-29 walk). Read every
  // spelling the records actually use.
  const nested = customer[`${kind}_address`];
  const nestedStreet = nested && typeof nested === 'object'
    ? (nested.street || nested.street1 || nested.line1 || nested.address1 || '')
    : '';
  if (nested && typeof nested === 'object' && (nestedStreet || nested.city || nested.zip)) {
    return {
      street: nestedStreet,
      street2: nested.street2 || nested.line2 || nested.address2 || '',
      city: nested.city || '',
      state: nested.state || '',
      zip: nested.zip || nested.postal || nested.postal_code || '',
    };
  }
  // Try shape 2: flat fields (`customer.address_street`, `customer.address_city`, ...).
  const prefix = kind === 'primary' ? 'address' : `${kind}_address`;
  const flat = {
    street: customer[`${prefix}_street`] || '',
    city: customer[`${prefix}_city`] || '',
    state: customer[`${prefix}_state`] || '',
    zip: customer[`${prefix}_zip`] || '',
  };
  if (flat.street || flat.city || flat.zip) return flat;

  // Q-16 (2026-08-18): live tenant 270 carries addresses in three additional
  // shapes that neither branch above recognises. Live observation: quote
  // 2216's customer card renders "12707 Old Henry Rd, Louisville, KY, 40223"
  // in full, but the customer-facing document rendered "Louisville KY 40223"
  // with no street — because `extractAddress` returned null and the fallback
  // to `quote.billing_address` was empty on this migrated record.
  //
  // Adding three shapes, mapped to the { street, city, state, zip } contract:
  //   (a) customer.addresses[0] = { line1, line2, city, state, zip }
  //   (b) customer.serviceAddress (or service_address) = { address1, ... }
  //   (c) customer.address (singular) = { street, city, state, zip }
  //
  // Only used for the 'billing' and 'primary' calls — the 'service' kind
  // has its own quote-scoped path in the caller and is not affected.
  if (kind === 'billing' || kind === 'primary') {
    // (a) canonical array shape
    const arr = Array.isArray(customer.addresses) ? customer.addresses : null;
    const first = arr && arr.length > 0 ? arr[0] : null;
    if (first) {
      const norm = {
        street: first.line1 || first.street || first.street1 || first.address1 || '',
        city: first.city || '',
        state: first.state || '',
        zip: first.zip || first.postal_code || '',
      };
      if (norm.street || norm.city || norm.zip) return norm;
    }
    // (b) legacy serviceAddress
    const serv = customer.serviceAddress || customer.service_address;
    if (serv && typeof serv === 'object') {
      const norm = {
        street: serv.address1 || serv.street || serv.line1 || '',
        city: serv.city || '',
        state: serv.state || '',
        zip: serv.zip || serv.postal_code || '',
      };
      if (norm.street || norm.city || norm.zip) return norm;
    }
    // (c) singular flat object
    const singular = customer.address;
    if (singular && typeof singular === 'object') {
      const norm = {
        street: singular.street || singular.address1 || singular.line1 || '',
        city: singular.city || '',
        state: singular.state || '',
        zip: singular.zip || singular.postal_code || '',
      };
      if (norm.street || norm.city || norm.zip) return norm;
    }
  }

  return null;
}
